import React, { useMemo, useState } from 'react';
import { CalendarDays, CheckCircle2, Clock3, Loader2, Pencil, RefreshCw, RotateCcw, XCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { CrmAvailabilitySettings } from './CrmAvailabilitySettings';

const STATUS_STYLES = {
  scheduled: 'bg-sky-500/15 text-sky-200 border-sky-500/30',
  confirmed: 'bg-emerald-500/15 text-emerald-200 border-emerald-500/30',
  completed: 'bg-violet-500/15 text-violet-200 border-violet-500/30',
  cancelled: 'bg-red-500/15 text-red-200 border-red-500/30',
  no_show: 'bg-amber-500/15 text-amber-200 border-amber-500/30',
};

const startOfWeek = (date) => {
  const next = new Date(date);
  const day = next.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  next.setDate(next.getDate() + diff);
  next.setHours(0, 0, 0, 0);
  return next;
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return date.toLocaleString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatDayLabel = (value) => {
  const date = new Date(value);
  return date.toLocaleDateString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

const buildEndTime = (startTime, currentStartTime, currentEndTime) => {
  if (!startTime) return currentEndTime || '';
  const start = new Date(startTime);
  if (Number.isNaN(start.getTime())) return currentEndTime || '';
  const fallbackDuration = 30 * 60 * 1000;
  const currentStart = currentStartTime ? new Date(currentStartTime) : null;
  const currentEnd = currentEndTime ? new Date(currentEndTime) : null;
  const durationMs = currentEnd && currentStart && !Number.isNaN(currentEnd.getTime()) && !Number.isNaN(currentStart.getTime())
    ? Math.max(currentEnd.getTime() - currentStart.getTime(), fallbackDuration)
    : fallbackDuration;
  return new Date(start.getTime() + durationMs).toISOString();
};

const AppointmentEditor = ({ appointment, saving, onClose, onSave }) => {
  const [draft, setDraft] = useState({
    customer_name: appointment.customer_name || '',
    customer_email: appointment.customer_email || '',
    customer_phone: appointment.customer_phone || '',
    start_time: appointment.start_time ? new Date(appointment.start_time).toISOString().slice(0, 16) : '',
    status: appointment.status || 'scheduled',
    notes: appointment.notes || '',
    resend_notifications: false,
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4" data-testid="crm-appointments-editor-modal">
      <div className="w-full max-w-xl rounded-3xl border border-white/10 bg-zinc-950 p-6 space-y-4 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-white">Edit appointment</h3>
            <p className="text-xs text-zinc-500 mt-1">Reschedule or update the meeting status without leaving CRM.</p>
          </div>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="crm-appointments-editor-close-button">✕</button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
          <div>
            <label className="block text-zinc-400 mb-1">Customer name</label>
            <input
              value={draft.customer_name}
              onChange={(e) => setDraft((prev) => ({ ...prev, customer_name: e.target.value }))}
              className="w-full h-9 rounded-xl bg-zinc-900 border border-zinc-800 px-3 text-white"
              data-testid="crm-appointments-editor-name-input"
            />
          </div>
          <div>
            <label className="block text-zinc-400 mb-1">Status</label>
            <select
              value={draft.status}
              onChange={(e) => setDraft((prev) => ({ ...prev, status: e.target.value }))}
              className="w-full h-9 rounded-xl bg-zinc-900 border border-zinc-800 px-3 text-white"
              data-testid="crm-appointments-editor-status-select"
            >
              <option value="scheduled">Scheduled</option>
              <option value="confirmed">Confirmed</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="no_show">No show</option>
            </select>
          </div>
          <div>
            <label className="block text-zinc-400 mb-1">Email</label>
            <input
              type="email"
              value={draft.customer_email}
              onChange={(e) => setDraft((prev) => ({ ...prev, customer_email: e.target.value }))}
              className="w-full h-9 rounded-xl bg-zinc-900 border border-zinc-800 px-3 text-white"
              data-testid="crm-appointments-editor-email-input"
            />
          </div>
          <div>
            <label className="block text-zinc-400 mb-1">Phone</label>
            <input
              value={draft.customer_phone}
              onChange={(e) => setDraft((prev) => ({ ...prev, customer_phone: e.target.value }))}
              className="w-full h-9 rounded-xl bg-zinc-900 border border-zinc-800 px-3 text-white"
              data-testid="crm-appointments-editor-phone-input"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-zinc-400 mb-1">Start time</label>
            <input
              type="datetime-local"
              value={draft.start_time}
              onChange={(e) => setDraft((prev) => ({ ...prev, start_time: e.target.value }))}
              className="w-full h-9 rounded-xl bg-zinc-900 border border-zinc-800 px-3 text-white"
              data-testid="crm-appointments-editor-start-input"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-zinc-400 mb-1">Notes</label>
            <textarea
              rows={3}
              value={draft.notes}
              onChange={(e) => setDraft((prev) => ({ ...prev, notes: e.target.value }))}
              className="w-full rounded-xl bg-zinc-900 border border-zinc-800 px-3 py-2 text-white resize-none"
              data-testid="crm-appointments-editor-notes-input"
            />
          </div>
        </div>

        <label className="flex items-center gap-2 text-xs text-zinc-300" data-testid="crm-appointments-editor-resend-toggle">
          <input
            type="checkbox"
            checked={draft.resend_notifications}
            onChange={(e) => setDraft((prev) => ({ ...prev, resend_notifications: e.target.checked }))}
          />
          Re-send the confirmation email/SMS after saving
        </label>

        <div className="flex items-center justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} data-testid="crm-appointments-editor-cancel-button">Cancel</Button>
          <Button
            type="button"
            onClick={() => onSave({
              customer_name: draft.customer_name,
              customer_email: draft.customer_email,
              customer_phone: draft.customer_phone,
              start_time: draft.start_time ? new Date(draft.start_time).toISOString() : undefined,
              end_time: draft.start_time ? buildEndTime(new Date(draft.start_time).toISOString(), appointment.start_time, appointment.end_time) : appointment.end_time,
              status: draft.status,
              notes: draft.notes,
              resend_notifications: draft.resend_notifications,
            })}
            disabled={saving}
            data-testid="crm-appointments-editor-save-button"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save changes
          </Button>
        </div>
      </div>
    </div>
  );
};

const formatNotificationLine = (appointment) => {
  const notifications = appointment.status === 'cancelled'
    ? (appointment.cancellation_notifications || {})
    : (appointment.notifications || {});
  return (
    <>
      Customer: Email {notifications?.email?.status || 'skipped'} · SMS {notifications?.sms?.status || 'skipped'}
      {' · '}
      Owner: Email {notifications?.owner_email?.status || 'skipped'} · SMS {notifications?.owner_sms?.status || 'skipped'}
    </>
  );
};

const CANCELLABLE_STATUSES = new Set(['scheduled', 'confirmed']);

export const AppointmentsPanel = ({ appointments, loading, saving, onRefresh, onUpdateAppointment, onCancelAppointment, token }) => {
  const [viewMode, setViewMode] = useState('agenda');
  const [weekOffset, setWeekOffset] = useState(0);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [cancellingId, setCancellingId] = useState('');

  const sortedAppointments = useMemo(
    () => [...appointments].sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime()),
    [appointments],
  );

  const stats = useMemo(() => {
    const now = Date.now();
    return {
      total: sortedAppointments.length,
      upcoming: sortedAppointments.filter((item) => {
        const startsAt = new Date(item.start_time).getTime();
        return startsAt >= now && item.status !== 'cancelled';
      }).length,
      synced: sortedAppointments.filter((item) => item.html_link).length,
      notifications: sortedAppointments.filter((item) => {
        const n = item.notifications || {};
        return ['email', 'sms', 'owner_email', 'owner_sms'].some((key) => n[key]?.status === 'sent');
      }).length,
    };
  }, [sortedAppointments]);

  const statCards = [
    { label: 'Total', value: stats.total, icon: <CalendarDays key="total-icon" className="w-4 h-4" /> },
    { label: 'Upcoming', value: stats.upcoming, icon: <Clock3 key="upcoming-icon" className="w-4 h-4" /> },
    { label: 'Calendar synced', value: stats.synced, icon: <CheckCircle2 key="synced-icon" className="w-4 h-4" /> },
    { label: 'Confirmations sent', value: stats.notifications, icon: <RotateCcw key="notifications-icon" className="w-4 h-4" /> },
  ];

  const weekDays = useMemo(() => {
    const base = startOfWeek(new Date());
    base.setDate(base.getDate() + (weekOffset * 7));
    return Array.from({ length: 7 }, (_, index) => {
      const day = new Date(base);
      day.setDate(base.getDate() + index);
      return day;
    });
  }, [weekOffset]);

  const groupedByDay = useMemo(() => {
    const map = new Map();
    weekDays.forEach((day) => map.set(day.toDateString(), []));
    sortedAppointments.forEach((appointment) => {
      const key = new Date(appointment.start_time).toDateString();
      if (map.has(key)) map.get(key).push(appointment);
    });
    return map;
  }, [sortedAppointments, weekDays]);

  const handleCancelMeeting = async (appointment) => {
    const when = formatDateTime(appointment.start_time);
    const name = appointment.customer_name || 'this customer';
    const confirmed = window.confirm(
      `Cancel the meeting with ${name} on ${when}?\n\nEmail and SMS cancellation notices will be sent to the customer and owner when configured.`,
    );
    if (!confirmed) return;

    setCancellingId(appointment.id);
    try {
      const updated = onCancelAppointment
        ? await onCancelAppointment(appointment.id)
        : await onUpdateAppointment(appointment.id, { status: 'cancelled' });
      if (updated) setEditingAppointment(null);
    } finally {
      setCancellingId('');
    }
  };

  return (
    <div className="space-y-5" data-testid="crm-appointments-panel">
      {editingAppointment ? (
        <AppointmentEditor
          appointment={editingAppointment}
          saving={saving}
          onClose={() => setEditingAppointment(null)}
          onSave={async (payload) => {
            await onUpdateAppointment(editingAppointment.id, payload);
            setEditingAppointment(null);
          }}
        />
      ) : null}

      <CrmAvailabilitySettings token={token} />

      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-white">Appointments</h2>
          <p className="text-xs text-zinc-500 mt-1">Review upcoming lead meetings in one place, then reschedule or update their status.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant={viewMode === 'agenda' ? 'default' : 'ghost'} onClick={() => setViewMode('agenda')} data-testid="crm-appointments-agenda-toggle">Agenda</Button>
          <Button type="button" variant={viewMode === 'calendar' ? 'default' : 'ghost'} onClick={() => setViewMode('calendar')} data-testid="crm-appointments-calendar-toggle">Calendar</Button>
          <Button type="button" variant="outline" onClick={onRefresh} data-testid="crm-appointments-refresh-button">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="crm-appointments-stats-grid">
        {statCards.map(({ label, value, icon }) => (
          <div key={label} className="rounded-2xl border border-white/10 bg-zinc-900/70 p-4" data-testid={`crm-appointments-stat-${String(label).toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="flex items-center justify-between text-zinc-400 text-xs uppercase tracking-[0.18em]">{label}<span>{icon}</span></div>
            <div className="mt-2 text-3xl font-semibold text-white">{value}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-400 text-sm" data-testid="crm-appointments-loading-state">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading appointments...
        </div>
      ) : sortedAppointments.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-zinc-700 bg-zinc-900/40 p-10 text-center" data-testid="crm-appointments-empty-state">
          <CalendarDays className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
          <p className="text-sm text-zinc-300">No appointments booked yet.</p>
          <p className="text-xs text-zinc-500 mt-2">Use the Book action on a CRM lead to create your first meeting.</p>
        </div>
      ) : viewMode === 'agenda' ? (
        <div className="space-y-3" data-testid="crm-appointments-agenda-list">
          {sortedAppointments.map((appointment) => (
            <div key={appointment.id} className="rounded-3xl border border-white/10 bg-zinc-900/60 p-5" data-testid={`crm-appointment-card-${appointment.id}`}>
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-base font-semibold text-white" data-testid={`crm-appointment-name-${appointment.id}`}>{appointment.customer_name || 'Unnamed appointment'}</h3>
                    <span className={`border rounded-full px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] ${STATUS_STYLES[appointment.status] || 'bg-zinc-800 text-zinc-300 border-zinc-700'}`} data-testid={`crm-appointment-status-${appointment.id}`}>{appointment.status || 'scheduled'}</span>
                  </div>
                  <p className="text-sm text-zinc-300" data-testid={`crm-appointment-time-${appointment.id}`}>{formatDateTime(appointment.start_time)}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-zinc-400">
                    <p data-testid={`crm-appointment-company-${appointment.id}`}>Company: {appointment.lead_company || '—'}</p>
                    <p data-testid={`crm-appointment-email-${appointment.id}`}>Email: {appointment.customer_email || '—'}</p>
                    <p>Phone: {appointment.customer_phone || '—'}</p>
                    <p>
                      {appointment.status === 'cancelled' ? 'Cancellation notices · ' : ''}
                      {formatNotificationLine(appointment)}
                    </p>
                  </div>
                  {appointment.notes ? <p className="text-xs text-zinc-500">Notes: {appointment.notes}</p> : null}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {appointment.html_link ? (
                    <a href={appointment.html_link} target="_blank" rel="noreferrer" className="px-3 py-2 rounded-xl border border-sky-500/30 bg-sky-500/10 text-sky-200 text-xs" data-testid={`crm-appointment-calendar-link-${appointment.id}`}>Calendar invite</a>
                  ) : null}
                  {CANCELLABLE_STATUSES.has(appointment.status || 'scheduled') ? (
                    <button
                      type="button"
                      onClick={() => handleCancelMeeting(appointment)}
                      disabled={Boolean(cancellingId) || saving}
                      className="inline-flex items-center gap-1 px-3 py-2 rounded-xl border border-red-500/30 bg-red-500/10 text-red-200 text-xs hover:bg-red-500/20 disabled:opacity-50"
                      data-testid={`crm-appointment-cancel-${appointment.id}`}
                    >
                      {cancellingId === appointment.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <XCircle className="w-3.5 h-3.5" />
                      )}
                      Cancel meeting
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => setEditingAppointment(appointment)}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-xl border border-white/10 bg-white/5 text-white text-xs hover:bg-white/10"
                    data-testid={`crm-appointment-edit-${appointment.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-3xl border border-white/10 bg-zinc-900/50 p-4 space-y-4" data-testid="crm-appointments-calendar-view">
          <div className="flex items-center justify-between gap-2">
            <button type="button" onClick={() => setWeekOffset((prev) => prev - 1)} className="px-3 py-2 rounded-xl border border-white/10 bg-white/5 text-xs hover:bg-white/10" data-testid="crm-appointments-prev-week-button">Previous week</button>
            <p className="text-xs text-zinc-400">{formatDayLabel(weekDays[0])} → {formatDayLabel(weekDays[6])}</p>
            <button type="button" onClick={() => setWeekOffset((prev) => prev + 1)} className="px-3 py-2 rounded-xl border border-white/10 bg-white/5 text-xs hover:bg-white/10" data-testid="crm-appointments-next-week-button">Next week</button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-7 gap-3">
            {weekDays.map((day) => {
              const key = day.toDateString();
              const items = groupedByDay.get(key) || [];
              return (
                <div key={key} className="rounded-2xl border border-white/10 bg-black/20 p-3 min-h-[180px]" data-testid={`crm-appointments-day-${day.toISOString().slice(0, 10)}`}>
                  <div className="mb-3">
                    <p className="text-xs uppercase tracking-[0.18em] text-zinc-500">{day.toLocaleDateString([], { weekday: 'short' })}</p>
                    <p className="text-sm font-semibold text-white">{day.toLocaleDateString([], { month: 'short', day: 'numeric' })}</p>
                  </div>
                  <div className="space-y-2">
                    {items.length === 0 ? (
                      <p className="text-xs text-zinc-600">No meetings</p>
                    ) : items.map((appointment) => (
                      <button
                        key={appointment.id}
                        type="button"
                        onClick={() => setEditingAppointment(appointment)}
                        className="w-full text-left rounded-2xl border border-white/10 bg-white/5 px-3 py-2 hover:bg-white/10"
                        data-testid={`crm-appointments-calendar-card-${appointment.id}`}
                      >
                        <p className="text-xs text-zinc-400">{new Date(appointment.start_time).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</p>
                        <p className="text-sm font-medium text-white mt-1">{appointment.customer_name || 'Unnamed'}</p>
                        <p className="text-xs text-zinc-500 mt-1">{appointment.lead_company || appointment.customer_email || 'CRM appointment'}</p>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};